/**
 * `/sign-in` — request a magic link.
 *
 * No password field — Eloquor uses magic links exclusively. On submit,
 * navigates to `/sign-in/sent` regardless of whether the email exists
 * (server returns 200 in both cases to avoid email-enumeration).
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from '../components/layout/AuthShell';
import { requestMagicLink } from '../api/auth';
import { ApiCallError } from '../api/client';

export function SignInPage() {
  const [params] = useSearchParams();
  const redirect = params.get('redirect') ?? undefined;
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    const form = new FormData(e.currentTarget);
    const email = String(form.get('email') ?? '');
    try {
      await requestMagicLink({ email, redirectTo: redirect });
      navigate('/sign-in/sent', { state: { email } });
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not send sign-in link.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell topRight={<Link to="/waitlist">Join waitlist</Link>}>
      <div className="auth-card">
        <div className="auth-eyebrow">
          <span className="num">05</span> · Sign in
        </div>
        <h1 className="auth-title">
          Welcome <em>back.</em>
        </h1>
        <p className="auth-sub">
          We'll email a one-time sign-in link. No passwords to remember.
        </p>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              className="input"
              id="email"
              name="email"
              type="email"
              placeholder="you@school.edu"
              required
              autoFocus
            />
          </div>

          {error && (
            <div className="helper err">
              <span className="dot" />
              <span>{error}</span>
            </div>
          )}

          <button className="btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Email me a link'} <span aria-hidden>→</span>
          </button>

          <div className="or-rule">
            <span>or</span>
          </div>

          <Link to="/invite" className="btn-ghost">
            Redeem an invite code
          </Link>
        </form>

        <p className="below-card">
          New to Eloquor? <Link to="/waitlist">Request access</Link>
        </p>
      </div>
    </AuthShell>
  );
}
